import { useCallback, useMemo } from 'react';

import { useGetVintedCatalogTreeQuery } from '@/shared/hooks/queries/generated';
import { POPULAR_BRANDS } from '@/shared/constants/popular-brands';
import type { CascaderOption, SelectOption } from '@/shared/types/options';
import { buildCatalogIdPathMap, toCascaderOptions } from '@/shared/utils/catalog-tree';

interface UseVintedOptionsReturn {
  categories: CascaderOption[];
  isLoading: boolean;
  marques: SelectOption[];
  getCategoryPaths: (ids: number[]) => number[][];
}

/**
 * Options du composeur de recherche.
 *
 * ⚠️ Ce hub exposait autrefois QUATORZE familles d'options chargées depuis des JSON français figés ;
 * douze n'avaient aucun consommateur. Il ne rend plus que ce que `SearchModal` utilise réellement, et
 * l'arbre catalogue vient désormais de l'API — donc localisé dans la langue du compte, et à jour.
 */
export const useVintedOptions = (): UseVintedOptionsReturn => {
  const { data, isLoading } = useGetVintedCatalogTreeQuery();

  const tree = useMemo(() => data?.getVintedCatalogTree ?? [], [data]);
  const categories = useMemo(() => toCascaderOptions(tree), [tree]);
  const idPathMap = useMemo(() => buildCatalogIdPathMap(tree), [tree]);

  // ⚠️ SYNCHRONE, contrairement à l'ancienne version : l'arbre est déjà en mémoire. L'appelant doit
  // toutefois attendre `isLoading === false`, sinon la carte est vide et les chemins valent `[id]`.
  const getCategoryPaths = useCallback(
    (ids: number[]): number[][] => ids.map((id) => idPathMap.get(id) ?? [id]),
    [idPathMap],
  );

  return { categories, isLoading, marques: POPULAR_BRANDS, getCategoryPaths };
};
